/**
 * Module-quiz page — Sprint 3.
 *
 * Hosts:
 *   1. ``ModuleQuiz`` (FR-E-1..3) — 10-item quiz player with cooldown UI
 *   2. ``ReflectionPrompt`` (FR-L / DEC-0014) — rendered after a passing
 *      submit; both Save and Skip dismiss it
 *   3. Post-reflection navigation — back to ``/course`` so the next
 *      module shows up unlocked
 *
 * Route: ``/module/:moduleSlug/quiz``. The slug is resolved against
 * ``GET /api/courses/cait-foundations/modules`` (Phase 0 has only one
 * course; later sprints generalize to ``/course/:courseSlug/...``).
 *
 * Auth handling mirrors ``LessonPlayer`` / ``CourseHome``: an
 * ApiError(401) on either initial fetch redirects to /api/auth/login
 * with ?return_to back to this page.
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ModuleQuiz } from "@/components/ModuleQuiz";
import { ReflectionPrompt } from "@/components/ReflectionPrompt";
import { API_BASE_URL, apiGet, ApiError } from "@/lib/api";
import type { SubmitModuleQuizResponse } from "@/lib/module-api";

interface ModuleSummary {
  id: string;
  sequence: number;
  slug: string;
  title: string;
}

/** FIX-PLAN-2026-06-02 Design A: lesson progress for this module, used
 *  by the soft gate to decide whether to surface the confirm modal. */
interface ModuleProgressSnapshot {
  lessons_completed: number;
  lessons_total: number;
  quiz_passed: boolean;
}

type LoadState =
  | { kind: "loading" }
  | {
      kind: "ready";
      module: ModuleSummary;
      progress: ModuleProgressSnapshot;
    }
  | { kind: "error"; message: string };

type PostQuizState =
  | { kind: "quiz" }
  | { kind: "reflection"; passResponse: SubmitModuleQuizResponse };

/** Subset of /api/courses/{slug}/progress used here. */
interface CourseProgressMini {
  modules: Array<{
    slug: string;
    lessons_completed: number;
    lessons_total: number;
    quiz_passed: boolean;
  }>;
}

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface ModuleQuizPageProps {
  /** Test seam. */
  onUnauthenticated?: (returnTo: string) => void;
}

export function ModuleQuizPage({
  onUnauthenticated = redirectToLogin,
}: ModuleQuizPageProps = {}): React.JSX.Element {
  const { moduleSlug } = useParams<{ moduleSlug: string }>();
  const navigate = useNavigate();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [stage, setStage] = useState<PostQuizState>({ kind: "quiz" });
  /** FIX-PLAN-2026-06-02 Design A: soft-gate confirm state.
   *  - null  = no confirm needed (all lessons done OR user already confirmed)
   *  - true  = show the modal (lessons incomplete, awaiting user choice)
   *  - false = user chose "take anyway", proceed to the quiz player
   */
  const [gateConfirmed, setGateConfirmed] = useState<boolean>(false);

  useEffect(() => {
    if (!moduleSlug) {
      setLoad({ kind: "error", message: "Module slug missing in URL." });
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        // Phase 0: only one course. Generalize when courses go beyond
        // cait-foundations. Fetch the module list + the user's progress
        // in parallel; the progress feed drives the soft-gate confirm.
        const [modules, progress] = await Promise.all([
          apiGet<ModuleSummary[]>("/api/courses/cait-foundations/modules"),
          apiGet<CourseProgressMini>(
            "/api/courses/cait-foundations/progress",
          ),
        ]);
        if (cancelled) return;
        const match = modules.find((m) => m.slug === moduleSlug);
        if (!match) {
          setLoad({ kind: "error", message: `Module "${moduleSlug}" not found.` });
          return;
        }
        const moduleProgress = progress.modules.find(
          (m) => m.slug === moduleSlug,
        );
        setLoad({
          kind: "ready",
          module: match,
          progress: moduleProgress ?? {
            lessons_completed: 0,
            lessons_total: 0,
            quiz_passed: false,
          },
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated(`/module/${moduleSlug}/quiz`);
          return;
        }
        const msg =
          err instanceof Error ? err.message : "Could not load the module.";
        setLoad({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleSlug, onUnauthenticated]);

  if (load.kind === "loading") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6" aria-busy="true">
        <p className="text-sm text-muted-foreground" role="status">
          Loading the module quiz…
        </p>
      </section>
    );
  }
  if (load.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground">
          Couldn't open the quiz
        </h1>
        <p className="mt-4 text-sm text-red-700" role="alert">
          {load.message}
        </p>
      </section>
    );
  }

  const { module, progress } = load;
  // FIX-PLAN-2026-06-02 Design A: soft gate. If the user hasn't marked
  // every lesson in this module complete AND hasn't already confirmed
  // they want to take the quiz anyway, show a confirm panel instead of
  // the quiz player. The user can either click "Take quiz anyway" to
  // proceed or "Back to lessons" to bail. This replaces the prior UX
  // where the quiz button was visible even mid-module — letting users
  // skip ahead silently — and instead surfaces the choice deliberately.
  // (Quiz unlock is gated server-side by the module being unlocked;
  // this is a content-readiness check, not a security check.)
  const needsConfirm =
    !gateConfirmed &&
    !progress.quiz_passed &&
    progress.lessons_total > 0 &&
    progress.lessons_completed < progress.lessons_total;

  return (
    <section className="mx-auto max-w-3xl space-y-8 px-4 py-12 sm:px-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Module {module.sequence}
        </p>
        <h1 className="mt-1 text-3xl font-bold text-foreground">
          {module.title} — module quiz
        </h1>
      </header>

      {needsConfirm ? (
        <article
          className="rounded-lg border border-amber-200 bg-amber-50 p-6"
          role="alertdialog"
          aria-labelledby="soft-gate-heading"
        >
          <h2
            id="soft-gate-heading"
            className="text-lg font-semibold text-amber-900"
          >
            You haven't finished all the lessons yet
          </h2>
          <p className="mt-2 text-sm text-amber-900">
            You've completed{" "}
            <span className="font-semibold">
              {progress.lessons_completed} of {progress.lessons_total}
            </span>{" "}
            lessons in this module. The quiz draws questions from the
            entire module — you'll likely do better if you finish the
            remaining lessons first. Want to take the quiz anyway?
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                void navigate("/course");
              }}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              ← Back to lessons
            </button>
            <button
              type="button"
              onClick={() => {
                setGateConfirmed(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              Take quiz anyway
            </button>
          </div>
        </article>
      ) : null}

      <ErrorBoundary label="the module quiz">
        {!needsConfirm ? (
          <ModuleQuiz
            moduleId={module.id}
            onPassed={(response) =>
              setStage({ kind: "reflection", passResponse: response })
            }
          />
        ) : null}

        {stage.kind === "reflection" ? (
          <ReflectionPrompt
            moduleId={module.id}
            moduleSlug={module.slug}
            moduleSequence={module.sequence}
            onDone={() => {
              // After Save or Skip, head back to the course home so the
              // unlocked next-module CTA is visible.
              void navigate("/course");
            }}
          />
        ) : null}
      </ErrorBoundary>
    </section>
  );
}
